import isEqual from 'lodash/isEqual';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { SelectionListState } from '../../../../shared/selection-lists/types';
import {
  createDefaultFilterMap,
  type DefaultSelectionOverride,
} from '../contexts/defaultFilters';
import { migrateLegacyPayDefaultMax } from '../definitions/pay';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import { getRoleTaxonomyValue } from '../hooks/useAdaptiveRoleTaxonomy';
import { getSkillTaxonomyValue } from '../hooks/useAdaptiveSkillTaxonomy';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type PrimaryFilterId,
  type PrimarySelection,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import { reviveFilterDates } from '../utils/revive-filter-dates';

export const NO_PRIMARY_FILTER_KEY = 'NO_PRIMARY_FILTER' as const;
export type ConsistencyKey = string;

const isPrimaryFilterKey = (
  key: ConsistencyKey | undefined
): key is PrimaryFilterId => {
  return (
    key === FilterId.COMPANY ||
    key === FilterId.INDUSTRY ||
    key === FilterId.GEOGRAPHY ||
    key === FilterId.ROLE ||
    key === FilterId.CUSTOM_SKILL ||
    key === FilterId.ACTIVITY
  );
};

export interface FilterConsistencyState {
  groupMap: Map<ConsistencyKey, FilterSelectionsMap>;
  activeKey?: ConsistencyKey;
  /**
   * Whether the active group's adaptive taxonomy is finalized — written
   * atomically with the taxonomy by `deriveAdaptiveTaxonomies`. `false` only
   * while a company is selected but the async deps (RICS tree / taxonomy
   * versions) haven't loaded. Composition-style queries pause on `!taxonomyReady`.
   */
  taxonomyReady?: boolean;
}

/**
 * Converts a ConsistencyKey to a PrimaryFilterId for use with createDefaultFilterMap.
 * Returns undefined for keys that don't name a primary filter — NO_PRIMARY_FILTER_KEY
 * and the page ids used by pages that key their filter group by page id.
 */
export const getPrimaryFilterIdFromConsistencyKey = (
  key: ConsistencyKey | undefined
): PrimaryFilterId | undefined => (isPrimaryFilterKey(key) ? key : undefined);

/**
 * Normalize a filter map on its way into state.
 *
 * Every path that loads a persisted map converges here: `handleReplaceFilters`
 * serves the deeplink restore and every `REPLACE_FILTERS` dispatcher (saved
 * filter sets included), and `addNewFilterGroup` serves additional deeplinked
 * groups. Per-filter normalizers therefore belong here rather than in any one
 * caller, which is how a saved set previously kept its raw ISO date strings.
 */
export const migrateFilterSelectionsMap = (
  map: FilterSelectionsMap
): FilterSelectionsMap =>
  new Map(
    Array.from(map.entries()).map(([id, filter]) => [
      id,
      reviveFilterDates(migrateLegacyPayDefaultMax(filter)),
    ])
  );

export const createInitialFilterConsistencyState = (
  defaultKey: ConsistencyKey,
  taxonomyConfig?: TaxonomyConfig,
  defaultSelections?: DefaultSelectionOverride
): FilterConsistencyState => {
  const initialMap = new Map<ConsistencyKey, FilterSelectionsMap>();

  initialMap.set(
    defaultKey,
    createDefaultFilterMap(
      getPrimaryFilterIdFromConsistencyKey(defaultKey),
      taxonomyConfig,
      defaultSelections
    )
  );

  return {
    groupMap: initialMap,
    activeKey: defaultKey,
  };
};

export const applyFilterAction = (
  state: FilterConsistencyState,
  filterIdToUpdate: FilterId,
  updatePayload: Partial<Filter> | Filter,
  isSetOperation: boolean,
  groupKeyOverride?: ConsistencyKey
): { nextState: FilterConsistencyState; updatedFilter: Filter | null } => {
  const { activeKey } = state;
  const groupKey = groupKeyOverride ?? activeKey;
  if (!groupKey) return { nextState: state, updatedFilter: null };

  const currentActiveMap = state.groupMap.get(groupKey);
  if (!currentActiveMap) return { nextState: state, updatedFilter: null };

  const newActiveMap = new Map(currentActiveMap);
  let updatedFilter: Filter | null = null;

  // SET_FILTER operation
  if (isSetOperation) {
    const filterToSet = updatePayload as Filter;
    newActiveMap.set(filterIdToUpdate, filterToSet);
    updatedFilter = filterToSet;
  } else {
    // UPDATE_FILTER operation
    const existingFilter = newActiveMap.get(filterIdToUpdate);
    if (existingFilter) {
      updatedFilter = { ...existingFilter, ...updatePayload } as Filter;
      newActiveMap.set(filterIdToUpdate, updatedFilter);
    }
  }

  if (updatedFilter) {
    const migratedFilter = migrateLegacyPayDefaultMax(updatedFilter);
    newActiveMap.set(filterIdToUpdate, migratedFilter);
    const newConsistencyMap = new Map(state.groupMap);
    newConsistencyMap.set(groupKey, newActiveMap);
    return {
      nextState: { ...state, groupMap: newConsistencyMap },
      updatedFilter: migratedFilter,
    };
  }

  return { nextState: state, updatedFilter: null };
};

const extractKeyFromFilters = (
  filters: FilterSelectionsMap | undefined
): PrimaryFilterId | undefined => {
  if (!filters) return undefined;

  return Array.from(filters.entries()).find(
    ([, f]) => 'isPrimary' in f && f.isPrimary === true
  )?.[0] as PrimaryFilterId | undefined;
};

export const getActiveFilterMap = (
  state: FilterConsistencyState,
  taxonomyConfig?: TaxonomyConfig
): FilterSelectionsMap => {
  if (!state.activeKey) {
    return createDefaultFilterMap(undefined, taxonomyConfig);
  }

  const activeMap = state.groupMap.get(state.activeKey);
  if (activeMap) {
    return activeMap;
  }

  if (!isPrimaryFilterKey(state.activeKey)) {
    return createDefaultFilterMap(undefined, taxonomyConfig);
  }

  return createDefaultFilterMap(
    getPrimaryFilterIdFromConsistencyKey(state.activeKey),
    taxonomyConfig
  );
};

export const getOrCreateFilters = (
  filterState: FilterConsistencyState,
  key: ConsistencyKey,
  taxonomyConfig?: TaxonomyConfig
): FilterSelectionsMap => {
  const existing = filterState.groupMap.get(key);
  if (existing) {
    return existing;
  }

  const filterMap = createDefaultFilterMap(
    getPrimaryFilterIdFromConsistencyKey(key),
    taxonomyConfig
  );

  return filterMap;
};

/**
 * Handles replacing the entire set of active filters for a consistency group.
 */
export const handleReplaceFilters = (
  state: FilterConsistencyState,
  payload: {
    filterMap: FilterSelectionsMap;
    consistencyKey: ConsistencyKey;
  }
): FilterConsistencyState => {
  const { filterMap: rawFilterMap, consistencyKey } = payload;

  const filterMap = migrateFilterSelectionsMap(rawFilterMap);

  const primaryFilterId = extractKeyFromFilters(filterMap);
  const activeKey = consistencyKey;

  const primaryFilter = primaryFilterId
    ? (filterMap.get(primaryFilterId) as TreeFilter | undefined)
    : undefined;
  const finalizedSelections = primaryFilter?.primarySelections || [];

  const finalGroupFilterMap = new Map(filterMap);
  if (primaryFilter && primaryFilterId) {
    finalGroupFilterMap.set(primaryFilterId, {
      ...primaryFilter,
      primarySelections: finalizedSelections,
      selections: finalizedSelections,
    });
  }

  const newGroupMap = new Map(state.groupMap);
  newGroupMap.set(activeKey, finalGroupFilterMap);

  if (
    state.activeKey === activeKey &&
    isEqual(state.groupMap.get(activeKey), finalGroupFilterMap)
  ) {
    return state;
  }

  return {
    ...state,
    groupMap: newGroupMap,
    activeKey,
  };
};

/**
 * Applies an update to a consistency group's primary selections. The callback
 * receives the group's current selections and returns the new list, or `null`
 * to leave the state untouched.
 */
export const updatePrimarySelectionsInGroup = (
  state: FilterConsistencyState,
  payload: { filterId: FilterId; groupKeyOverride?: ConsistencyKey },
  updateFn: (currentSelections: PrimarySelection[]) => PrimarySelection[] | null
): FilterConsistencyState => {
  const { filterId } = payload;
  const { activeKey, groupMap } = state;

  const groupKey = payload.groupKeyOverride ?? activeKey;

  if (!groupKey) return state;

  const currentGroupMap = groupMap.get(groupKey);
  const currentGroupFilter = currentGroupMap?.get(filterId);
  if (!currentGroupFilter || currentGroupFilter.type !== FilterType.TREE) {
    return state;
  }

  const currentSelections = currentGroupFilter.primarySelections ?? [];

  const newSelections = updateFn(currentSelections);

  if (newSelections === null || newSelections === currentSelections) {
    return state;
  }

  const newGroupFilter = {
    ...currentGroupFilter,
    primarySelections: newSelections,
    selections: newSelections,
  };
  const newGroupMapForActiveKey = new Map(currentGroupMap);
  newGroupMapForActiveKey.set(filterId, newGroupFilter);
  const newGroupMap = new Map(groupMap);
  newGroupMap.set(groupKey, newGroupMapForActiveKey);

  return {
    ...state,
    groupMap: newGroupMap,
  };
};

export const addNewFilterGroup = ({
  filterState,
  newFilterKey,
  newFilterMap,
}: {
  filterState: FilterConsistencyState;
  newFilterKey: ConsistencyKey;
  newFilterMap: FilterSelectionsMap;
}): FilterConsistencyState => {
  const newGroupMap = new Map(filterState.groupMap);
  newGroupMap.set(newFilterKey, migrateFilterSelectionsMap(newFilterMap));

  return { ...filterState, groupMap: newGroupMap };
};

export const createNewFilterState = ({
  primaryFilterId,
  taxonomyConfig,
  ricsK400List,
  defaultSelections,
}: {
  primaryFilterId: PrimaryFilterId;
  taxonomyConfig?: TaxonomyConfig;
  ricsK400List?: SelectionListState<'rics_k400'>['rics_k400'];
  defaultSelections?: DefaultSelectionOverride;
}): FilterConsistencyState => {
  const defaultFilterState = createInitialFilterConsistencyState(
    primaryFilterId,
    taxonomyConfig,
    defaultSelections
  );

  const filterMap = defaultFilterState.groupMap.get(primaryFilterId);

  const filterState = filterMap
    ? handleReplaceFilters(defaultFilterState, {
        filterMap: filterMap,
        consistencyKey: primaryFilterId,
      })
    : defaultFilterState;

  const adaptiveTaxonomyFilterState = (() => {
    if (primaryFilterId !== FilterId.COMPANY) {
      return null;
    }

    const filterSelectionsMap = filterState.groupMap?.get(FilterId.COMPANY);
    if (!filterSelectionsMap) return null;

    const companyFilterState = getTreeFilter(
      filterSelectionsMap,
      FilterId.COMPANY
    );

    if (!companyFilterState) return null;

    const roleTaxonomyValue = {
      ...getRoleTaxonomyValue(companyFilterState, ricsK400List ?? []),
      version: taxonomyConfig?.roleTaxonomyVersion,
    };

    const newFilterMap = new Map(filterState.groupMap.get(FilterId.COMPANY));
    const defaultRoleTaxonomyFilter = newFilterMap.get(
      FilterId.ROLE_TAXONOMY
    ) as SingleValueFilter<CustomRoleTaxonomySelection> | undefined;
    if (!defaultRoleTaxonomyFilter) return null;

    newFilterMap.set(FilterId.ROLE_TAXONOMY, {
      ...defaultRoleTaxonomyFilter,
      value: roleTaxonomyValue,
    });

    const defaultSkillTaxonomyFilter = newFilterMap.get(
      FilterId.SKILL_TAXONOMY
    ) as SingleValueFilter<CustomSkillTaxonomySelection> | undefined;

    if (!defaultSkillTaxonomyFilter) return null;

    const skillTaxonomyValue = {
      ...getSkillTaxonomyValue(companyFilterState, ricsK400List ?? []),
      version: taxonomyConfig?.skillTaxonomyVersion,
    };

    newFilterMap.set(FilterId.SKILL_TAXONOMY, {
      ...defaultSkillTaxonomyFilter,
      value: skillTaxonomyValue,
      isUserSubmitted: false,
    });

    return newFilterMap;
  })();

  return adaptiveTaxonomyFilterState
    ? handleReplaceFilters(filterState, {
        filterMap: adaptiveTaxonomyFilterState,
        consistencyKey: primaryFilterId,
      })
    : filterState;
};

const getTreeFilter = (
  filterState: FilterSelectionsMap,
  filterId: FilterId
) => {
  const filter = filterState.get(filterId);
  if (!filter || filter.type !== FilterType.TREE) return null;
  return filter;
};
